import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  duration?: number;
  startOnMount?: boolean;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}

export function useCountUp(
  end: number,
  options: UseCountUpOptions = {}
) {
  const {
    duration = 1000,
    startOnMount = true,
    decimals = 0,
    prefix = '',
    suffix = ''
  } = options;

  const [count, setCount] = useState(startOnMount ? 0 : end);
  const [isAnimating, setIsAnimating] = useState(false);
  const frameRef = useRef<number>();
  const startTimeRef = useRef<number>();

  const animate = (timestamp: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
    
    // Easing function for smooth animation
    const easeOutQuart = 1 - Math.pow(1 - progress, 4);
    
    const currentCount = end * easeOutQuart;
    setCount(currentCount);

    if (progress < 1) {
      frameRef.current = requestAnimationFrame(animate);
    } else {
      setIsAnimating(false);
      setCount(end);
    }
  };

  const start = () => {
    setIsAnimating(true);
    startTimeRef.current = undefined;
    frameRef.current = requestAnimationFrame(animate);
  };

  const reset = () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    setCount(0);
    setIsAnimating(false);
    startTimeRef.current = undefined;
  };

  useEffect(() => {
    if (startOnMount) {
      start();
    }

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, startOnMount]);

  const formattedValue = `${prefix}${count.toFixed(decimals)}${suffix}`;

  return {
    count,
    formattedValue,
    isAnimating,
    start,
    reset
  };
}
